/* eslint-disable no-nested-ternary */
import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { flexbox, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import { selectPaymentAccounts } from 'redux-api/reselect/billing';
import ContentLoader from 'theme/content-loader';
import PaymentAccountCard from 'theme/payment-account-card';
import Text from 'theme/text';
import BtnLink from 'theme/buttons/btn-link';
import Plus from 'theme/icons/plus';
import translations from 'translations';
import AlertMessage from 'theme/alert-message';
import Card from 'theme/card';

const Section = styled.div`
  width: 100%;
  max-width: 2100px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${flexbox}
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: ${themeGet('space.m')}px 0;
  padding: ${themeGet('space.s')}px;
  background: ${themeGet('colors.primary_100')};
  border: 1px solid ${themeGet('colors.primary_200')};
  border-radius: 12px;
  overflow: auto;

  ${layout}
`;

const WrapEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 220px;
`;

function PaymentAccounts({ selected, errors, noTotal, onCardClick, onClickNew, edit, company }) {
  const { push } = useRouter();

  const { languageCode } = useSelector((state) => state.config);
  const { isWaiting } = useSelector((state) => state.billing);
  const payload = useSelector(selectPaymentAccounts);

  const handleOnClickNew = useCallback(() => {
    if (onClickNew) return onClickNew();

    return push('/billing/payment-account');
  }, [onClickNew, push]);

  return (
    <Section>
      <Row justifyContent="space-between">
        <Row>
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
            {translations[languageCode]['Payment methods']}
          </Text>

          {!noTotal && (
            <Text pl="xxs" fontSize="h3" lineHeight="h3" fontWeight="bold" color="primary_400">
              {payload.total}
            </Text>
          )}
        </Row>

        <Row>
          <BtnLink
            label={translations[languageCode]['Create new']}
            icon={<Plus />}
            onClick={handleOnClickNew}
          />
        </Row>
      </Row>
      {errors && (errors.paymentAccountPid || errors.countryCode) && (
        <Row>
          <Card width="100%" borderColor="orange_400" bg="accent_light" p="m" my="l">
            {Object.entries(errors).map(([key, value]) => (
              <AlertMessage key={key} message={value} />
            ))}
          </Card>
        </Row>
      )}

      <CardsContainer minHeight="148px">
        {isWaiting ? (
          <ContentLoader
            width={808}
            height={148}
            lines={[
              {
                x: 0,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 148,
              },
              {
                x: 408,
                y: 0,
                rx: 5,
                ry: 5,
                width: 400,
                height: 148,
              },
            ]}
          />
        ) : payload.paymentAccounts.length === 0 ? (
          <WrapEmpty>
            <Text
              pl="xxs"
              fontSize="pSmall"
              lineHeight="pSmall"
              fontWeight="normal"
              color="primary_600"
            >
              {translations[languageCode]["You don't have a “Payment Methods” yet"]}
            </Text>
          </WrapEmpty>
        ) : (
          payload.paymentAccounts.map((paymentAccount) => (
            <PaymentAccountCard
              paymentAccount={paymentAccount}
              key={paymentAccount.id}
              selected={selected === paymentAccount.id}
              errors={errors}
              style={{
                mr: 'xs',
                maxWidth: '400px',
                border: selected === paymentAccount.id ? '2px solid' : '1px solid',
                borderColor: selected === paymentAccount.id ? 'primary_500' : 'primary_200',
              }}
              {...paymentAccount}
              languageCode={languageCode}
              onClick={onCardClick}
              edit={edit}
              company={company}
            />
          ))
        )}
      </CardsContainer>
    </Section>
  );
}

export default PaymentAccounts;
